import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { actions as dialogActions } from "@Store/Reducers/dialog";
import {
    Grid,
    MenuItem,
    TextField,
    Typography,
    Button,
    Box,
    FormControlLabel,
    Checkbox,
    Link,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { Controller, useForm } from "react-hook-form";
import {
    IAuthor,
    IChannelSettings,
    IGuestData,
    IPoll,
    RootStateType,
} from "@Interfaces";
import { PollActions } from "@Store";
import UserAvatar from "@Components/PollItem/UserAvatar";
import { useTranslation } from "react-i18next";
import useMobileDropdownInputProps from "@Hooks/useMobileDropdownInputProps";
const ageRange = ["18-24", "25-34", "35-44", "45-54", "55-64", "65+"];
const listGender = [
    { value: "he_him", label: "He/him" },
    { value: "she_her", label: "She/her" },
    { value: "they_them", label: "They/them" },
];
const EMAIL_PATTERN = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;

interface SemanticsTaxonomy {
    sectors?: string[];
    industries?: string[];
    professions?: string[];
    roles?: Record<string, string> | string[];
    organizationTypes?: string[];
    countries?: string[];
    nationalities?: string[];
}

interface IDataCollectDialogProps {
    settings: IChannelSettings;
    author: IAuthor;
}

type GuestFormData = IGuestData & {
    agree?: boolean;
};

const DataCollectionContent: React.FC<IDataCollectDialogProps> = ({
    author,
    settings,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<any>();
    const currentPoll = useSelector(
        (state: RootStateType) => state.poll
    ) as IPoll | null;
    const [taxonomy, setTaxonomy] = useState<SemanticsTaxonomy | null>(null);
    const semanticsUrl = `${
        process.env.PUBLIC_URL || ""
    }/semantics/semantics.json`;
    useEffect(() => {
        let isMounted = true;
        fetch(semanticsUrl)
            .then((response) => {
                if (!response.ok) {
                    throw new Error("Failed to load semantics taxonomy");
                }
                return response.json();
            })
            .then((data) => {
                if (isMounted) {
                    setTaxonomy(data);
                }
            })
            .catch(() => {
                if (isMounted) {
                    setTaxonomy(null);
                }
            });
        return () => {
            isMounted = false;
        };
    }, [semanticsUrl]);
    // Destructure settings using cammel case
    const {
        is_email_required: isEmailRequired,
        voters_vp: voterMsg,
        cta_msg: ctaMsg,
        demographic_fields: demographicFields,
        custom_variables: rawCustomVariables,
        custom_text_field_enabled: customTextFieldEnabled,
        custom_text_field_question: customTextFieldQuestion,
    } = settings || {};

    const collectSector = !!demographicFields?.sector;
    const collectIndustry = !!demographicFields?.industry;
    const collectProfession = !!demographicFields?.profession;
    const collectRole = !!demographicFields?.role;
    const collectAgeRange = !!demographicFields?.age;
    const collectOrganizationType = !!demographicFields?.organization_type;
    const collectCountry = !!demographicFields?.country;
    const collectNationality = !!demographicFields?.nationality;
    const collectGender = !!demographicFields?.gender;
    const customVariableDefinitions = useMemo(
        () =>
            (rawCustomVariables || [])
                .filter(
                    (variable) =>
                        !!variable?.label &&
                        (variable.custom_options || []).length > 0
                )
                .map((variable) => ({
                    label: variable.label as string,
                    options: (variable.custom_options || []).filter(
                        (option) => !!option
                    ) as string[],
                })),
        [rawCustomVariables]
    );
    const hasCustomVariables = customVariableDefinitions.length > 0;
    const customTextFieldQuestionText = customTextFieldQuestion?.trim() ?? "";
    const hasActiveDemographicField =
        collectSector ||
        collectIndustry ||
        collectProfession ||
        collectRole ||
        collectOrganizationType ||
        collectCountry ||
        collectNationality ||
        collectAgeRange ||
        collectGender;
    const sectorOptions = taxonomy?.sectors ?? [];
    const industryOptions = taxonomy?.industries ?? [];
    const professionOptions = taxonomy?.professions ?? [];
    const organizationTypeOptions = taxonomy?.organizationTypes ?? [];
    const countryOptions = taxonomy?.countries ?? [];
    const nationalityOptions = taxonomy?.nationalities ?? [];
    const roleOptions = useMemo(() => {
        if (!taxonomy?.roles) return [];
        return Array.isArray(taxonomy.roles)
            ? taxonomy.roles
            : Object.keys(taxonomy.roles);
    }, [taxonomy]);
    const { mobileDropdownInputProps } = useMobileDropdownInputProps();

    const defaultCustomVariables = useMemo(() => {
        if (!hasCustomVariables) {
            return undefined;
        }
        return customVariableDefinitions.reduce((accumulator, variable) => {
            accumulator[variable.label] = "";
            return accumulator;
        }, {} as Record<string, string>);
    }, [customVariableDefinitions, hasCustomVariables]);

    const {
        handleSubmit,
        control,
        watch,
        formState: { errors },
        trigger,
        clearErrors,
    } = useForm<GuestFormData>({
        defaultValues: {
            sector: "",
            industry: "",
            profession: "",
            role: "",
            organization_type: "",
            country: "",
            nationality: "",
            date_range: "",
            gender: "",
            email: "",
            custom_variables: defaultCustomVariables,
            custom_text_field_response: "",
        },
    });

    // Watch fields dynamically

    const email = watch("email");
    const agree = watch("agree", false);
    const sector = watch("sector");
    const industry = watch("industry");
    const profession = watch("profession");
    const role = watch("role");
    const organization_type = watch("organization_type");
    const country = watch("country");
    const nationality = watch("nationality");
    const date_range = watch("date_range");
    const gender = watch("gender");

    const customVariableValues = watch("custom_variables") || {};

    const isButtonDisabled =
        (!hasActiveDemographicField &&
            !isEmailRequired &&
            !hasCustomVariables) ||
        (hasActiveDemographicField &&
            ((collectSector && !sector) ||
                (collectIndustry && !industry) ||
                (collectProfession && !profession) ||
                (collectRole && !role) ||
                (collectOrganizationType && !organization_type) ||
                (collectCountry && !country) ||
                (collectNationality && !nationality) ||
                (collectAgeRange && !date_range) ||
                (collectGender && !gender))) ||
        (hasCustomVariables &&
            customVariableDefinitions.some(
                (variable) => !customVariableValues[variable.label]
            )) ||
        (email?.trim() !== "" && !agree) || // Disable if email has value and checkbox is not checked
        !!errors.email;

    const onSubmit = (data: GuestFormData) => {
        const demographic = {
            ...(collectSector && data.sector && { sector: data.sector }),
            ...(collectIndustry &&
                data.industry && { industry: data.industry }),
            ...(collectProfession &&
                data.profession && { profession: data.profession }),
            ...(collectRole && data.role && { role: data.role }),
            ...(collectOrganizationType &&
                data.organization_type && {
                    organization_type: data.organization_type,
                }),
            ...(collectCountry && data.country && { country: data.country }),
            ...(collectNationality &&
                data.nationality && { nationality: data.nationality }),
            ...(collectAgeRange &&
                data.date_range && { date_range: data.date_range }),
            ...(collectGender && data.gender && { gender: data.gender }),
        };

        const guestData: {
            demographic?: typeof demographic;
            email?: string;
            custom_variables?: Record<string, string>;
            custom_text_field_response?: string;
            custom_text_field_question?: string;
        } = {};

        if (Object.keys(demographic).length > 0) {
            guestData.demographic = demographic;
        }

        if (data.email) {
            guestData.email = data.email;
        }

        if (hasCustomVariables) {
            const sanitizedCustomVariables = customVariableDefinitions.reduce(
                (accumulator, variable) => {
                    const value =
                        data.custom_variables?.[variable.label]?.trim();
                    if (value) {
                        accumulator[variable.label] = value;
                    }
                    return accumulator;
                },
                {} as Record<string, string>
            );

            if (Object.keys(sanitizedCustomVariables).length > 0) {
                guestData.custom_variables = sanitizedCustomVariables;
            }
        }

        if (customTextFieldEnabled && data.custom_text_field_response?.trim()) {
            guestData.custom_text_field_response =
                data.custom_text_field_response.trim();
            if (customTextFieldQuestionText) {
                guestData.custom_text_field_question =
                    customTextFieldQuestionText;
            }
        }

        // This is required for only the first time the user voted and gave us data,
        // only here we need to patch the vote with the extra data then it gets saved
        if (currentPoll?.vote_id && !localStorage.getItem("guest-data")) {
            dispatch(
                PollActions.updateVote(currentPoll._id, {
                    ...guestData,
                    vote_id: currentPoll.vote_id,
                })
            );
        }

        dispatch(dialogActions.hideDialog());
        localStorage.setItem("guest-data", JSON.stringify(guestData));
    };

    return (
        <Box component="div" sx={{ px: 1 }}>
            {voterMsg && (
                <Box
                    sx={{
                        height: "auto",
                        textAlign: "center",
                        padding: "15px 0 16px 0",
                    }}
                >
                    <Typography style={{ fontSize: 16 }}>{voterMsg}</Typography>
                </Box>
            )}
            <Grid container spacing={1} sx={{ pt: voterMsg ? 0 : 1 }}>
                <Grid item xs={12}>
                    <form noValidate onSubmit={handleSubmit(onSubmit)}>
                        <Grid container spacing={1}>
                            {isEmailRequired && (
                                <Grid item xs={12}>
                                    <Controller
                                        name="email"
                                        control={control}
                                        defaultValue=""
                                        rules={{
                                            validate: (value?: string) =>
                                                value === "" ||
                                                EMAIL_PATTERN.test(
                                                    value ?? ""
                                                ) ||
                                                "Enter a valid email address",
                                        }}
                                        render={({
                                            field: { value, onChange },
                                        }) => (
                                            <TextField
                                                value={value}
                                                onChange={(event) => {
                                                    const newValue =
                                                        event.target.value;
                                                    onChange(newValue); // Update the email value
                                                    // Clear the error if the email is empty or valid
                                                    if (
                                                        newValue.trim() ===
                                                            "" ||
                                                        EMAIL_PATTERN.test(
                                                            newValue
                                                        )
                                                    ) {
                                                        clearErrors("email");
                                                    }
                                                }}
                                                onBlur={() => trigger("email")} // Trigger validation on blur
                                                label={t(
                                                    "dialogs.data_collection.email_label"
                                                )}
                                                type="email"
                                                fullWidth
                                                variant="outlined"
                                                error={!!errors.email}
                                                helperText={
                                                    errors.email?.message
                                                }
                                            />
                                        )}
                                    />
                                    {email && (
                                        <FormControlLabel
                                            control={
                                                <Controller
                                                    name="agree"
                                                    control={control}
                                                    defaultValue={false}
                                                    render={({
                                                        field: {
                                                            value,
                                                            onChange,
                                                        },
                                                    }) => (
                                                        <Checkbox
                                                            checked={value}
                                                            onChange={(event) =>
                                                                onChange(
                                                                    event.target
                                                                        .checked
                                                                )
                                                            }
                                                            color="primary"
                                                        />
                                                    )}
                                                />
                                            }
                                            label={
                                                <Typography
                                                    variant="body2"
                                                    style={{
                                                        fontSize: "0.8rem",
                                                        marginTop: "1px",
                                                        marginBottom: "1px",
                                                        textAlign: "justify",
                                                    }}
                                                >
                                                    {t(
                                                        "dialogs.data_collection.email_consent",
                                                        {
                                                            author: author?.username,
                                                        }
                                                    )}{" "}
                                                    <Link
                                                        href="mailto:privacy@tendbe.com"
                                                        underline="hover"
                                                    >
                                                        privacy@tendbe.com
                                                    </Link>{" "}
                                                    {t(
                                                        "dialogs.data_collection.privacy_policy_text"
                                                    )}
                                                    <Link
                                                        href="https://tendbe.com/terms"
                                                        underline="hover"
                                                    >
                                                        {t(
                                                            "dialogs.data_collection.privacy_policy_link"
                                                        )}
                                                    </Link>
                                                    .
                                                </Typography>
                                            }
                                        />
                                    )}
                                </Grid>
                            )}

                            {customTextFieldEnabled && (
                                <Grid item xs={12}>
                                    <Controller
                                        name="custom_text_field_response"
                                        control={control}
                                        defaultValue=""
                                        render={({
                                            field: { value, onChange },
                                        }) => (
                                            <TextField
                                                value={value || ""}
                                                onChange={onChange}
                                                label={
                                                    customTextFieldQuestionText ||
                                                    t(
                                                        "dialogs.channel_settings.custom_text_field"
                                                    )
                                                }
                                                variant="outlined"
                                                fullWidth
                                            />
                                        )}
                                    />
                                </Grid>
                            )}
                            {hasActiveDemographicField && (
                                <>
                                    {collectGender && (
                                        <Grid item xs={12}>
                                            <Controller
                                                name="gender"
                                                control={control}
                                                defaultValue=""
                                                render={({
                                                    field: { value, onChange },
                                                }) => (
                                                    <TextField
                                                        value={value}
                                                        onChange={onChange}
                                                        select
                                                        sx={{
                                                            textAlign: "left",
                                                        }}
                                                        label={t(
                                                            "dialogs.channel_settings.demographics.gender"
                                                        )}
                                                        variant="outlined"
                                                        fullWidth
                                                    >
                                                        {listGender.map(
                                                            (entry) => (
                                                                <MenuItem
                                                                    key={
                                                                        entry.value
                                                                    }
                                                                    value={
                                                                        entry.value
                                                                    }
                                                                >
                                                                    {
                                                                        entry.label
                                                                    }
                                                                </MenuItem>
                                                            )
                                                        )}
                                                    </TextField>
                                                )}
                                            />
                                        </Grid>
                                    )}
                                    {collectAgeRange && (
                                        <Grid item xs={12}>
                                            <Controller
                                                name="date_range"
                                                control={control}
                                                defaultValue=""
                                                render={({
                                                    field: { value, onChange },
                                                }) => (
                                                    <TextField
                                                        value={value}
                                                        onChange={onChange}
                                                        sx={{
                                                            textAlign: "left",
                                                        }}
                                                        select
                                                        label={t(
                                                            "dialogs.channel_settings.demographics.age"
                                                        )}
                                                        variant="outlined"
                                                        fullWidth
                                                    >
                                                        {ageRange.map(
                                                            (value, index) => (
                                                                <MenuItem
                                                                    key={index}
                                                                    value={
                                                                        value
                                                                    }
                                                                >
                                                                    {value}
                                                                </MenuItem>
                                                            )
                                                        )}
                                                    </TextField>
                                                )}
                                            />
                                        </Grid>
                                    )}
                                    {collectCountry && (
                                        <Grid item xs={12}>
                                            <Controller
                                                name="country"
                                                control={control}
                                                defaultValue=""
                                                render={({
                                                    field: { value, onChange },
                                                }) => (
                                                    <Autocomplete
                                                        openOnFocus
                                                        options={countryOptions}
                                                        value={value ?? null}
                                                        onChange={(
                                                            _,
                                                            newValue
                                                        ) =>
                                                            onChange(
                                                                newValue ?? ""
                                                            )
                                                        }
                                                        renderInput={(
                                                            params
                                                        ) => (
                                                            <TextField
                                                                {...params}
                                                                label={t(
                                                                    "dialogs.channel_settings.demographics.country"
                                                                )}
                                                                variant="outlined"
                                                                fullWidth
                                                                inputProps={{
                                                                    ...params.inputProps,
                                                                    ...mobileDropdownInputProps,
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                    )}
                                    {collectNationality && (
                                        <Grid item xs={12}>
                                            <Controller
                                                name="nationality"
                                                control={control}
                                                defaultValue=""
                                                render={({
                                                    field: { value, onChange },
                                                }) => (
                                                    <Autocomplete
                                                        openOnFocus
                                                        options={
                                                            nationalityOptions
                                                        }
                                                        value={value ?? null}
                                                        onChange={(
                                                            _,
                                                            newValue
                                                        ) =>
                                                            onChange(
                                                                newValue ?? ""
                                                            )
                                                        }
                                                        renderInput={(
                                                            params
                                                        ) => (
                                                            <TextField
                                                                {...params}
                                                                label={t(
                                                                    "dialogs.channel_settings.demographics.nationality"
                                                                )}
                                                                variant="outlined"
                                                                fullWidth
                                                                inputProps={{
                                                                    ...params.inputProps,
                                                                    ...mobileDropdownInputProps,
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                    )}
                                    {collectSector && (
                                        <Grid item xs={12}>
                                            <Controller
                                                name="sector"
                                                control={control}
                                                defaultValue=""
                                                render={({
                                                    field: { value, onChange },
                                                }) => (
                                                    <Autocomplete
                                                        openOnFocus
                                                        options={sectorOptions}
                                                        value={value ?? null}
                                                        onChange={(
                                                            _,
                                                            newValue
                                                        ) =>
                                                            onChange(
                                                                newValue ?? ""
                                                            )
                                                        }
                                                        renderInput={(
                                                            params
                                                        ) => (
                                                            <TextField
                                                                {...params}
                                                                label={t(
                                                                    "dialogs.channel_settings.demographics.sector"
                                                                )}
                                                                variant="outlined"
                                                                fullWidth
                                                                inputProps={{
                                                                    ...params.inputProps,
                                                                    ...mobileDropdownInputProps,
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                    )}
                                    {collectIndustry && (
                                        <Grid item xs={12}>
                                            <Controller
                                                name="industry"
                                                control={control}
                                                defaultValue=""
                                                render={({
                                                    field: { value, onChange },
                                                }) => (
                                                    <Autocomplete
                                                        openOnFocus
                                                        options={
                                                            industryOptions
                                                        }
                                                        value={value ?? null}
                                                        onChange={(
                                                            _,
                                                            newValue
                                                        ) =>
                                                            onChange(
                                                                newValue ?? ""
                                                            )
                                                        }
                                                        renderInput={(
                                                            params
                                                        ) => (
                                                            <TextField
                                                                {...params}
                                                                label={t(
                                                                    "dialogs.channel_settings.demographics.industry"
                                                                )}
                                                                variant="outlined"
                                                                fullWidth
                                                                inputProps={{
                                                                    ...params.inputProps,
                                                                    ...mobileDropdownInputProps,
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                    )}
                                    {collectOrganizationType && (
                                        <Grid item xs={12}>
                                            <Controller
                                                name="organization_type"
                                                control={control}
                                                defaultValue=""
                                                render={({
                                                    field: { value, onChange },
                                                }) => (
                                                    <Autocomplete
                                                        openOnFocus
                                                        options={
                                                            organizationTypeOptions
                                                        }
                                                        value={value ?? null}
                                                        onChange={(
                                                            _,
                                                            newValue
                                                        ) =>
                                                            onChange(
                                                                newValue ?? ""
                                                            )
                                                        }
                                                        renderInput={(
                                                            params
                                                        ) => (
                                                            <TextField
                                                                {...params}
                                                                label={t(
                                                                    "dialogs.channel_settings.demographics.organization_type"
                                                                )}
                                                                variant="outlined"
                                                                fullWidth
                                                                inputProps={{
                                                                    ...params.inputProps,
                                                                    ...mobileDropdownInputProps,
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                    )}
                                    {collectProfession && (
                                        <Grid item xs={12}>
                                            <Controller
                                                name="profession"
                                                control={control}
                                                defaultValue=""
                                                render={({
                                                    field: { value, onChange },
                                                }) => (
                                                    <Autocomplete
                                                        openOnFocus
                                                        options={
                                                            professionOptions
                                                        }
                                                        value={value ?? null}
                                                        onChange={(
                                                            _,
                                                            newValue
                                                        ) =>
                                                            onChange(
                                                                newValue ?? ""
                                                            )
                                                        }
                                                        renderInput={(
                                                            params
                                                        ) => (
                                                            <TextField
                                                                {...params}
                                                                label={t(
                                                                    "dialogs.channel_settings.demographics.profession"
                                                                )}
                                                                variant="outlined"
                                                                fullWidth
                                                                inputProps={{
                                                                    ...params.inputProps,
                                                                    ...mobileDropdownInputProps,
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                    )}
                                    {collectRole && (
                                        <Grid item xs={12}>
                                            <Controller
                                                name="role"
                                                control={control}
                                                defaultValue=""
                                                render={({
                                                    field: { value, onChange },
                                                }) => (
                                                    <Autocomplete
                                                        openOnFocus
                                                        options={roleOptions}
                                                        value={value ?? null}
                                                        onChange={(
                                                            _,
                                                            newValue
                                                        ) =>
                                                            onChange(
                                                                newValue ?? ""
                                                            )
                                                        }
                                                        renderInput={(
                                                            params
                                                        ) => (
                                                            <TextField
                                                                {...params}
                                                                label={t(
                                                                    "dialogs.channel_settings.demographics.role"
                                                                )}
                                                                variant="outlined"
                                                                fullWidth
                                                                inputProps={{
                                                                    ...params.inputProps,
                                                                    ...mobileDropdownInputProps,
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                    )}
                                </>
                            )}
                            {hasCustomVariables &&
                                customVariableDefinitions.map((variable) => (
                                    <Grid item xs={12} key={variable.label}>
                                        <Controller
                                            name={
                                                `custom_variables.${variable.label}` as any
                                            }
                                            control={control}
                                            rules={{
                                                required: true,
                                            }}
                                            render={({
                                                field: { value, onChange },
                                            }) => (
                                                <TextField
                                                    value={value || ""}
                                                    onChange={onChange}
                                                    sx={{ textAlign: "left" }}
                                                    select
                                                    label={variable.label}
                                                    variant="outlined"
                                                    fullWidth
                                                >
                                                    {variable.options.map(
                                                        (option) => (
                                                            <MenuItem
                                                                key={option}
                                                                value={option}
                                                            >
                                                                {option}
                                                            </MenuItem>
                                                        )
                                                    )}
                                                </TextField>
                                            )}
                                        />
                                    </Grid>
                                ))}
                            <Grid
                                item
                                xs={12}
                                sx={{
                                    margin: "auto",
                                    textAlign: "center",
                                    color: "grey.600",
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                <Button
                                    disabled={isButtonDisabled}
                                    color="primary"
                                    size="large"
                                    type="submit"
                                    variant="contained"
                                >
                                    {ctaMsg ||
                                        t(
                                            "dialogs.data_collection.continue_button"
                                        )}
                                </Button>
                            </Grid>
                        </Grid>
                    </form>
                </Grid>
            </Grid>
        </Box>
    );
};

const DataCollectionHeader: React.FC<IDataCollectDialogProps> = ({
    author,
    settings,
}) => {
    const displayProfilePicture = settings?.display_profile_picture ?? true;
    const avatarSize = 120;

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                width: "100%",
            }}
        >
            <Box
                sx={{
                    width: avatarSize,
                    height: avatarSize,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <UserAvatar
                    username={author?.username}
                    avatar={author?.avatar}
                    size="large"
                    disablePointer
                    displayProfilePicture={displayProfilePicture}
                />
            </Box>
        </Box>
    );
};

export default {
    content: DataCollectionContent,
    header: DataCollectionHeader,
    showHeader: true,
    disableCancelButton: true,
    isGuestData: false,
};
